import React, { useState } from 'react';
import { Calendar, Clock, MapPin, User, Phone, Mail, Truck, CheckCircle, Building, FileText, Plus, Minus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Vehicle options
const VEHICLE_OPTIONS = [
  { value: 'lorry-crane-20', label: 'Lorry Crane - 20 Ton' },
  { value: 'lorry-crane-25', label: 'Lorry Crane - 25 Ton' },
  { value: 'lorry-crane-40', label: 'Lorry Crane - 40 Ton' },
  { value: 'lorry-crane-50', label: 'Lorry Crane - 50 Ton' },
  { value: 'lorry-crane-55', label: 'Lorry Crane - 55 Ton (With Winch)' },
  { value: 'lorry-crane-75', label: 'Lorry Crane - 75 Ton' },
  { value: 'lorry-crane-100', label: 'Lorry Crane - 100 Ton' },
  { value: 'trailer-40ft', label: '40ft Trailer' },
  { value: 'trailer-lowbed', label: 'Lowbed Trailer' },
  { value: 'tailgate-14', label: '14FT Tailgate' },
  { value: 'tailgate-24', label: '24FT Tailgate' },
  { value: 'manpower', label: 'Manpower' },
  { value: 'warehouse-storage', label: 'Warehouse Storage' },
];

export const Booking = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedBookingId, setSubmittedBookingId] = useState(null);
  const [formData, setFormData] = useState({
    // Company Details
    company_name: '',
    company_contact: '',
    company_email: '',
    company_address: '',
    // Booking Details (Contact Person)
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    // Job Date & Time
    date: '',
    time: '',
    // Site Contact
    site_contact: '',
    site_phone: '',
    // Additional Info
    additional_info: ''
  });

  // Multiple vehicle slots - each with multiple unload points
  const [vehicleSlots, setVehicleSlots] = useState([
    { 
      transport_type: '', 
      qty: '1', 
      rate_type: 'per_trip', 
      pickup_address: '', 
      unload_points: [''] // Array of unload addresses
    }
  ]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlotChange = (index, field, value) => {
    const newSlots = [...vehicleSlots];
    newSlots[index] = { ...newSlots[index], [field]: value };
    setVehicleSlots(newSlots);
  };

  // Add unload point to a specific vehicle
  const addUnloadPoint = (vehicleIndex) => {
    const newSlots = [...vehicleSlots];
    if (newSlots[vehicleIndex].unload_points.length < 5) {
      newSlots[vehicleIndex].unload_points.push('');
      setVehicleSlots(newSlots);
    }
  };

  // Remove unload point from a specific vehicle
  const removeUnloadPoint = (vehicleIndex, pointIndex) => {
    const newSlots = [...vehicleSlots];
    if (newSlots[vehicleIndex].unload_points.length > 1) {
      newSlots[vehicleIndex].unload_points = newSlots[vehicleIndex].unload_points.filter((_, i) => i !== pointIndex);
      setVehicleSlots(newSlots);
    }
  };

  // Update unload point address
  const updateUnloadPoint = (vehicleIndex, pointIndex, value) => {
    const newSlots = [...vehicleSlots];
    newSlots[vehicleIndex].unload_points[pointIndex] = value;
    setVehicleSlots(newSlots);
  };

  const addVehicleSlot = () => {
    if (vehicleSlots.length < 5) {
      setVehicleSlots([...vehicleSlots, { 
        transport_type: '', 
        qty: '1', 
        rate_type: 'per_trip', 
        pickup_address: '', 
        unload_points: [''] 
      }]);
    }
  };

  const removeVehicleSlot = (index) => {
    if (vehicleSlots.length > 1) {
      setVehicleSlots(vehicleSlots.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare vehicle slots data
      const preparedSlots = vehicleSlots.map(slot => ({
        ...slot,
        unload_address: slot.unload_points.filter(p => p).join(' → '), // Join all points with arrow
        unload_points: slot.unload_points.filter(p => p)
      }));

      const submitData = {
        ...formData,
        vehicle_slots: preparedSlots.filter(slot => slot.transport_type),
        // For backward compatibility
        transport_type: vehicleSlots[0]?.transport_type || '',
        vehicle_qty: vehicleSlots[0]?.qty || '1',
        rate_type: vehicleSlots[0]?.rate_type || 'per_trip',
        pickup_address: vehicleSlots[0]?.pickup_address || '',
        unload_address: vehicleSlots[0]?.unload_points.filter(p => p).join(' → ') || '',
        num_locations: vehicleSlots[0]?.unload_points.filter(p => p).length || 1
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/bookings/`, submitData);
      
      if (response.data.success) {
        // Defensive: ensure message is a string before toasting (avoids React #31 crash)
        const msg = response.data.message;
        const safeMsg = typeof msg === 'string' ? msg : 'Booking submitted successfully!';
        toast.success(safeMsg);
        // Defensive: ensure booking_id is a primitive (string/number), never an object
        const rawId = response.data.booking_id;
        const safeId = (typeof rawId === 'string' || typeof rawId === 'number') ? String(rawId) : 'submitted';
        setSubmittedBookingId(safeId);
        // Reset form fields
        setFormData({
          company_name: '',
          company_contact: '',
          company_email: '',
          company_address: '',
          customer_name: '',
          customer_phone: '',
          customer_email: '',
          date: '',
          time: '',
          site_contact: '',
          site_phone: '',
          additional_info: ''
        });
        setVehicleSlots([{ 
          transport_type: '', 
          qty: '1', 
          rate_type: 'per_trip', 
          pickup_address: '', 
          unload_points: [''] 
        }]);
        // Scroll to top so user clearly sees the thank-you state
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) {}
      }
    } catch (error) {
      // Defensive: FastAPI 422 validation returns detail as an ARRAY of objects.
      // Passing an object/array to toast or rendering it causes React error #31 (blank page).
      let errorMessage = 'Something went wrong. Please try again.';
      const detail = error?.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail
          .map((e) => (typeof e === 'string' ? e : (e?.msg || JSON.stringify(e))))
          .join('; ');
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || JSON.stringify(detail);
      } else if (typeof error?.message === 'string') {
        errorMessage = error.message;
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Truck Background */}
      <section className="pt-32 pb-16 px-4 relative overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1762060853805-ac5a37f5de6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxoZWF2eSUyMHRydWNrJTIwb24lMjByb2FkJTIwbG9naXN0aWNzJTIwdHJhbnNwb3J0fGVufDB8fHx8MTc3MjQ1MjgyN3ww&ixlib=rb-4.1.0&q=85')`
          }}
        />
        {/* Overlay */}
        <div className="absolute inset-0 bg-burgundy-deep/75" />
        
        <div className="container mx-auto text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {submittedBookingId ? 'Booking Received!' : 'Book Your Service'}
          </h1>
          <p className="text-xl text-white/70 max-w-3xl mx-auto">
            {submittedBookingId
              ? "Thank you — we've received your booking and will be in touch shortly."
              : 'Fill in the details below to request our heavy vehicle logistics services'}
          </p>
        </div>
      </section>

      {/* Success State — shown after submission */}
      {submittedBookingId && (
        <section className="py-12 px-4" data-testid="booking-success">
          <div className="container mx-auto max-w-2xl">
            <Card className="border-2 border-green-200 bg-green-50/40">
              <CardContent className="p-8 text-center">
                <CheckCircle size={64} className="mx-auto text-green-600 mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-3">Booking Submitted Successfully!</h2>
                <p className="text-gray-700 mb-2">
                  Thank you for choosing <strong>NJK Logistics</strong>.
                </p>
                <p className="text-gray-600 mb-6">
                  Your booking request has been received. Our team will review the details and contact you shortly to confirm.
                </p>
                <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6 text-left">
                  <p className="text-sm text-gray-500 mb-1">What happens next?</p>
                  <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
                    <li>You'll receive a confirmation email shortly.</li>
                    <li>Our team will call/WhatsApp you to confirm pricing &amp; availability.</li>
                    <li>Once confirmed, you'll receive a delivery order with full details.</li>
                  </ol>
                </div>
                <p className="text-sm text-gray-600 mb-6">
                  Need to speak to us urgently? Call <a href="tel:98366186" className="text-burgundy font-semibold underline">98366186</a> or email <a href="mailto:inquiry@njklogistics.com" className="text-burgundy font-semibold underline">inquiry@njklogistics.com</a>
                </p>
                <Button
                  onClick={() => setSubmittedBookingId(null)}
                  className="bg-burgundy hover:bg-burgundy-light"
                  data-testid="make-another-booking"
                >
                  Make Another Booking
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Booking Form (hidden after successful submission) */}
      {!submittedBookingId && (
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-4xl">
          <Card className="border-2">
            <CardHeader>
              <CardTitle className="text-2xl">Booking Request Form</CardTitle>
              <CardDescription>Please provide all required information for your booking</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-8">
                
                {/* Section 1: Company Details */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Building size={20} className="text-burgundy" />
                    Company Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="companyName">Company Name *</Label>
                      <Input
                        id="companyName"
                        placeholder="ABC Construction Pte Ltd"
                        value={formData.company_name}
                        onChange={(e) => handleChange('company_name', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyContact">Company Contact *</Label>
                      <Input
                        id="companyContact"
                        type="tel"
                        placeholder="6512345678"
                        value={formData.company_contact}
                        onChange={(e) => handleChange('company_contact', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyEmail">Company Email *</Label>
                      <Input
                        id="companyEmail"
                        type="email"
                        placeholder="info@company.com"
                        value={formData.company_email}
                        onChange={(e) => handleChange('company_email', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="companyAddress">Company Address</Label>
                      <Input
                        id="companyAddress"
                        placeholder="123 Main Street, Singapore"
                        value={formData.company_address}
                        onChange={(e) => handleChange('company_address', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Order Person Details */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <User size={20} className="text-burgundy" />
                    Order Person Details
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="customerName">Person-in-Charge Name *</Label>
                      <Input
                        id="customerName"
                        placeholder="John Doe"
                        value={formData.customer_name}
                        onChange={(e) => handleChange('customer_name', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="customerPhone">Contact Number *</Label>
                      <Input
                        id="customerPhone"
                        type="tel"
                        placeholder="91234567"
                        value={formData.customer_phone}
                        onChange={(e) => handleChange('customer_phone', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label htmlFor="customerEmail">Contact Email *</Label>
                      <Input
                        id="customerEmail"
                        type="email"
                        placeholder="john@company.com"
                        value={formData.customer_email}
                        onChange={(e) => handleChange('customer_email', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Job Date & Time */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Calendar size={20} className="text-burgundy" />
                    Job Date & Time
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="date">Job Date *</Label>
                      <Input
                        id="date"
                        type="date"
                        value={formData.date}
                        onChange={(e) => handleChange('date', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="time">Job Time *</Label>
                      <Input
                        id="time"
                        type="time"
                        value={formData.time}
                        onChange={(e) => handleChange('time', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Section 4: Vehicle Slots */}
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold flex items-center gap-2">
                      <Truck size={20} className="text-burgundy" />
                      Vehicle Requirements
                    </h3>
                    {vehicleSlots.length < 5 && (
                      <Button type="button" variant="outline" size="sm" onClick={addVehicleSlot}>
                        <Plus size={16} className="mr-1" />
                        Add Vehicle
                      </Button>
                    )}
                  </div>

                  <div className="space-y-6">
                    {vehicleSlots.map((slot, vIndex) => (
                      <div key={vIndex} className="p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-medium text-gray-700">
                            Vehicle {vIndex + 1}
                            {vIndex > 0 && (
                              <span className="ml-2 text-xs bg-gold/15 text-burgundy px-2 py-0.5 rounded">Additional</span>
                            )}
                          </span>
                          {vIndex > 0 && (
                            <Button type="button" variant="ghost" size="sm" onClick={() => removeVehicleSlot(vIndex)} className="text-red-600 h-8 w-8 p-0">
                              <Minus size={16} />
                            </Button>
                          )}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                          <div className="space-y-2">
                            <Label>Vehicle Type *</Label>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              value={slot.transport_type}
                              onChange={(e) => handleSlotChange(vIndex, 'transport_type', e.target.value)}
                              required
                              data-testid={`vehicle-type-${vIndex}`}
                            >
                              <option value="">Select vehicle</option>
                              {VEHICLE_OPTIONS.map(option => (
                                <option key={option.value} value={option.value}>{option.label}</option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label>Quantity *</Label>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              value={slot.qty}
                              onChange={(e) => handleSlotChange(vIndex, 'qty', e.target.value)}
                              data-testid={`vehicle-qty-${vIndex}`}
                            >
                              {[1,2,3,4,5].map(n => (
                                <option key={n} value={n.toString()}>{n}</option>
                              ))}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <Label>Rate Type *</Label>
                            <select
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                              value={slot.rate_type}
                              onChange={(e) => handleSlotChange(vIndex, 'rate_type', e.target.value)}
                              data-testid={`rate-type-${vIndex}`}
                            >
                              <option value="per_trip">Per Trip</option>
                              <option value="hourly">Hourly</option>
                              <option value="per_day">Per Day (1-Day)</option>
                            </select>
                          </div>
                        </div>

                        {/* Pickup Address */}
                        <div className="space-y-2 mb-4">
                          <Label className="flex items-center gap-2">
                            <MapPin size={14} className="text-gold-dark" />
                            Pickup Address (Point A) *
                          </Label>
                          <Input
                            placeholder="e.g., 123 Industrial Road"
                            value={slot.pickup_address}
                            onChange={(e) => handleSlotChange(vIndex, 'pickup_address', e.target.value)}
                            required
                          />
                        </div>

                        {/* Unload Points */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-2">
                              <MapPin size={14} className="text-red-600" />
                              Unloading Points
                            </Label>
                            {slot.unload_points.length < 5 && (
                              <Button 
                                type="button" 
                                variant="outline" 
                                size="sm" 
                                onClick={() => addUnloadPoint(vIndex)}
                                className="text-xs h-7"
                              >
                                <Plus size={14} className="mr-1" />
                                Add Point
                              </Button>
                            )}
                          </div>
                          
                          {slot.unload_points.map((point, pIndex) => (
                            <div key={pIndex} className="flex items-center gap-2">
                              <span className="text-sm font-medium text-gray-500 w-16">
                                Point {String.fromCharCode(66 + pIndex)}
                              </span>
                              <Input
                                placeholder={`Unloading Point ${String.fromCharCode(66 + pIndex)}`}
                                value={point}
                                onChange={(e) => updateUnloadPoint(vIndex, pIndex, e.target.value)}
                                className="flex-1"
                                required={pIndex === 0}
                              />
                              {pIndex > 0 && (
                                <Button 
                                  type="button" 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => removeUnloadPoint(vIndex, pIndex)}
                                  className="text-red-600 h-8 w-8 p-0"
                                >
                                  <Minus size={14} />
                                </Button>
                              )}
                            </div>
                          ))}
                          
                          {slot.unload_points.length >= 5 && (
                            <p className="text-xs text-gray-500">Maximum 5 unloading points per vehicle</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {vehicleSlots.length >= 5 && (
                    <p className="text-sm text-gray-500 mt-2">Maximum 5 vehicles per booking</p>
                  )}
                </div>

                {/* Section 5: Site Contact */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Phone size={20} className="text-burgundy" />
                    Site Contact
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="siteContact">Site Contact Person *</Label>
                      <Input
                        id="siteContact"
                        placeholder="Site supervisor name"
                        value={formData.site_contact}
                        onChange={(e) => handleChange('site_contact', e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="sitePhone">Site Contact Number *</Label>
                      <Input
                        id="sitePhone"
                        type="tel"
                        placeholder="98765432"
                        value={formData.site_phone}
                        onChange={(e) => handleChange('site_phone', e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Section 6: Additional Info */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FileText size={20} className="text-gray-600" />
                    Additional Information
                  </h3>
                  <Textarea
                    placeholder="Any special requirements, cargo details, access restrictions, etc."
                    value={formData.additional_info}
                    onChange={(e) => handleChange('additional_info', e.target.value)}
                    rows={4}
                  />
                </div>

                {/* Submit Button */}
                <div className="pt-6">
                  <Button
                    type="submit"
                    size="lg"
                    className="w-full bg-burgundy hover:bg-burgundy-light text-white"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <CheckCircle size={20} />
                        Submit Booking Request
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
      )}
    </div>
  );
};
